import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import type { SelfSignUpForm } from '../types';
import { clearPersistedFormData, getPersistedFormData, saveFormData, type PersistedFormData } from './useSelfSignUpFormPersistence';
import { getProductPlanRoute, type ProductPlan } from '../productPlan';

export type SelfSignUpRecoveryFormState = {
  initialStepIndex: number;
  initialValues: Partial<SelfSignUpForm>;
};

type SelfSignUpRouteState = {
  initialStepIndex?: number;
  initialValues?: Partial<SelfSignUpForm>;
  skipRecovery?: boolean;
};

const buildInitialValues = (
  initialValues: Partial<SelfSignUpForm> | undefined,
  productPlan: ProductPlan,
): Partial<SelfSignUpForm> => ({
  ...initialValues,
  productPlan,
});

const buildRouteFormState = (
  routeState: SelfSignUpRouteState | undefined,
  productPlan: ProductPlan,
): SelfSignUpRecoveryFormState | null => {
  if (!routeState?.skipRecovery) return null;

  return {
    initialStepIndex: routeState.initialStepIndex ?? 0,
    initialValues: buildInitialValues(routeState.initialValues, productPlan),
  };
};

const buildRecoveryFormState = (
  initialStepIndex: number,
  initialValues: Partial<SelfSignUpForm> | undefined,
  productPlan: ProductPlan,
): SelfSignUpRecoveryFormState => ({
  initialStepIndex,
  initialValues: buildInitialValues(initialValues, productPlan),
});

const useSelfSignUpRecovery = (productPlan: ProductPlan) => {
  const history = useHistory();
  const location = useLocation<SelfSignUpRouteState>();
  const routeState = location.state;
  const routeFormState = useMemo(
    () => buildRouteFormState(routeState, productPlan),
    [routeState, productPlan],
  );

  const [savedData, setSavedData] = useState<PersistedFormData | null>(null);
  const [formState, setFormState] = useState<SelfSignUpRecoveryFormState | null>(null);

  useEffect(() => {
    let isActive = true;

    if (routeFormState) {
      setSavedData(null);
      setFormState(routeFormState);
      window.history.replaceState({}, '', location.pathname);

      return () => {
        isActive = false;
      };
    }

    setFormState(null);
    getPersistedFormData(productPlan)
      .then((data) => {
        if (isActive) setSavedData(data);
      })
      .catch(() => {
        if (isActive) setSavedData(null);
      });

    return () => {
      isActive = false;
    };
  }, [location.key, location.pathname, productPlan, routeFormState]);

  const handleRecover = useCallback(() => {
    if (!savedData) return;

    setFormState(buildRecoveryFormState(savedData.stepIndex, savedData.values, productPlan));
  }, [productPlan, savedData]);

  const handleDeclineRecovery = useCallback(() => {
    clearPersistedFormData();
    setFormState(buildRecoveryFormState(0, undefined, productPlan));
  }, [productPlan]);

  const handleSwitchPlan = useCallback((nextProductPlan: ProductPlan, values: Partial<SelfSignUpForm>) => {
    const nextValues = buildInitialValues(values, nextProductPlan);

    saveFormData(nextValues, 0);
    history.push(getProductPlanRoute(nextProductPlan), {
      initialStepIndex: 0,
      initialValues: nextValues,
      skipRecovery: true,
    });
  }, [history]);

  return {
    formState: routeFormState ?? formState,
    handleDeclineRecovery,
    handleRecover,
    handleSwitchPlan,
    savedData: routeFormState ? null : savedData,
  };
};

export default useSelfSignUpRecovery;
